'use client';

import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';

export type Currency = 'USD' | 'ZWL';

interface CurrencyContextValue {
  currency: Currency;
  setCurrency: (c: Currency) => void;
  formatAmount: (n: number) => string;
  currencySymbol: string;
}

const CurrencyContext = createContext<CurrencyContextValue>({
  currency: 'USD',
  setCurrency: () => {},
  formatAmount: (n) => `USD ${n.toFixed(2)}`,
  currencySymbol: 'USD',
});

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const [currency, setCurrency] = useState<Currency>('USD');

  const currencySymbol = currency === 'USD' ? 'USD' : 'ZWL';

  const formatAmount = useCallback((n: number): string => {
    const sym = currency === 'USD' ? 'USD' : 'ZWL';
    const formatted = n.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${sym} ${formatted}`;
  }, [currency]);

  const value = useMemo(() => ({ currency, setCurrency, formatAmount, currencySymbol }), [currency, formatAmount, currencySymbol]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}
